import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import BackupRestoreForm from 'routes/BackupRestore/components/BackupRestoreForm';
import { Ref } from 'routes/BackupRestore/components/BackupRestoreForm/BackupRestoreForm';
import { RootState } from 'data/root.reducers';
import * as backupActions from 'data/backupRestore/backup.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import { backupTemplate } from '../../templates';

type RouterParams = {
  history: string
}

interface BackupCreateFormData {
  product: string;
  owner: string;
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
});

const mapDispatch = {
  requestBackup: backupActions.requestBackup,
  goToBackupRestoreList: backupActions.goToBackupRestoreList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const BackupRestoreCreate = ({
  history,
  userId,
  goToBackupRestoreList,
  requestBackup,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<BackupCreateFormData>>({ product: 'xcipio', owner: '' });
  const [template, setTemplate] = useState(backupTemplate);
  const { t } = useTranslation();

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData);
    const clonedTemplate = cloneDeep(template);

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate.key = Date.now().toString();

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const handleClickSave = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const payload = formRef.current.getValues();

    if (payload) {
      requestBackup({ ...payload, owner: userId }, history);
    }
  };

  const handleCancel = () => goToBackupRestoreList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <BackupRestoreForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="BackupCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('backupRestore.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(BackupRestoreCreate));
